import React, { useState, useEffect, useCallback } from "react";
import Confetti from "react-confetti";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Form,
  Modal,
  Card,
  message,
  Upload,
  Radio,
  Steps,
  Statistic,
  Result,
  Progress,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import axios from "axios";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import { BeatLoader } from "react-spinners";
import { css } from "@emotion/react";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
} from "firebase/storage";
import { initializeApp } from "firebase/app";

import firebaseConfig from "../firebaseConfig";
import { firebaseAPI } from "../firebaseConfig";
import axiosInstance from "../utils/axiosInstance"; // Adjust path if needed

const Countdown = Statistic.Countdown;

const FormStatus = Object.freeze({
  IN_PROGRESS: 0,
  COMPLETED: 1,
  TIMEOUT: 2,
  ERROR: 3,
  NO_FORM_ROUND: 4,
});
function logTimerDebugInfo({
  clientUtcNow,
  serverTimestamp,
  offset,
  target,
  remaining,
}) {
  console.log("===== 🕒 Time Debug Info =====");
  console.log("📍 Client UTC Now:", new Date(clientUtcNow).toISOString());
  console.log(
    "📍 Server UTC Timestamp:",
    new Date(serverTimestamp).toISOString()
  );
  console.log("📉 Clock Offset (client - server):", offset, "ms");
  console.log(
    "🎯 Target End Time (Client UTC):",
    new Date(target).toISOString()
  );
  console.log("⏳ Time Remaining:", remaining, "ms");
  console.log(
    "⏳ Time Remaining (formatted):",
    Math.floor(remaining / 60000),
    "min",
    Math.floor((remaining % 60000) / 1000),
    "sec"
  );
}

function logAutoSubmitCheck({
  clientNow,
  adjustedUtcNow,
  formTimerTarget,
  remaining,
}) {
  console.log("===== 🔄 Auto-Submit Check =====");
  console.log("📍 Client Now:", new Date(clientNow).toISOString());
  console.log(
    "🛠 Server-Adjusted UTC Now:",
    new Date(adjustedUtcNow).toISOString()
  );
  console.log("🎯 Target End Time:", new Date(formTimerTarget).toISOString());
  console.log("⏳ Time Remaining (ms):", remaining);
  console.log(
    "⌛ Time Remaining (formatted):",
    Math.floor(remaining / 60000) + " min",
    Math.floor((remaining % 60000) / 1000) + " sec"
  );
}

const { Step } = Steps;
const { Title } = Typography;
const { Dragger } = Upload;
const override = css`
  display: block;
  margin: 0 auto;
  border-color: red;
`;
const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

// Function to upload a file
const uploadFileToFirebase = (file, onProgress) => {
  return new Promise((resolve, reject) => {
    const uid = uuidv4();
    const uniqueFileName = `${uid}-${file.name}`;
    const storageRef = ref(storage, `ur-uploads/${uniqueFileName}`);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress); // Notify caller
        }
      },
      (error) => {
        console.error("Error uploading file:", error);
        reject(error);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(url);
      }
    );
  });
};

const deleteFileFromFirebase = async (fileUrl) => {
  const fileRef = ref(storage, fileUrl);
  await deleteObject(fileRef);
};
let uploadedFilesUrls = [];

const QuizAndCVPage = () => {
  const { programName, formName } = useParams();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [cvFile, setCvFile] = useState(null);
  const [cvFileName, setCvFileName] = useState("");
  const [formComplete, setFormComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [videoFile, setVideoFile] = useState(null);
  const [videoFileName, setVideoFileName] = useState("");
  const [formTimerTarget, setFormTimerTarget] = useState(-1);
  const [formStatus, setFormStatus] = useState(FormStatus.IN_PROGRESS);
  const [errorMessage, setErrorMessage] = useState("");
  const [questionKey, setQuestionKey] = useState(0); // Initialize the key for question component

  // Add state to prevent multiple submissions
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  const showConfirm = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    handleFormSubmit();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };
  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAnswerChange = (index, value) => {
    answers[index] = value;
    setAnswers(answers);
    checkFormComplete();

    // Increment the key to force the question component to re-render
    setQuestionKey((prevKey) => prevKey + 1);
  };

  const handleVideoUpload = (file) => {
    const isMP4 = file.type === "video/mp4";
    const isFileSizeValid = file.size / 1024 / 1024 < 300; // < 200 MB
    console.log(file.size / 1024 / 1024);
    if (!isMP4 || !isFileSizeValid) {
      message.error("Please upload an MP4 under 200 MB.");
      // LIST_IGNORE prevents AntD from adding it to the fileList
      return Upload.LIST_IGNORE;
    }
    const max_duration = 180;
    // Check duration
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.src = url;

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        if (video.duration <= max_duration) {
          // all good!
          setVideoFile(file);
          setVideoFileName(file.name);
          resolve(true); // allow add to fileList
        } else {
          message.error("Video duration exceeds " + max_duration + " seconds.");
          resolve(Upload.LIST_IGNORE);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        message.error("Could not read video metadata.");
        resolve(Upload.LIST_IGNORE);
      };
    });
  };
  const handleFileUpload = async (file) => {
    const isPDForWord =
      file.type === "application/pdf" ||
      file.type === "application/msword" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const isFileSizeValid = file.size / 1024 / 1024 < 5; // Max file size 5 MB
    if (isPDForWord && isFileSizeValid) {
      ////console.log(file)
      setCvFile(file);
      setCvFileName(file.name);
    } else {
      message.error(
        "Please upload a file in PDF or Word format and less than 5 MB in size."
      );
    }
  };

  const checkFormComplete = () => {
    const allAnswersGiven = answers.every((answer) => answer !== "");
    setFormComplete(allAnswersGiven);
  };
  const handleFormSubmit = useCallback(
    async (isAutoSubmit = false) => {
      console.log("handleFormSubmit called", {
        isAutoSubmit,
        isSubmitting,
        hasAutoSubmitted,
      });
      // Prevent duplicate submissions
      if (isSubmitting || (isAutoSubmit && hasAutoSubmitted)) {
        console.log("Submission skipped: duplicate or already auto-submitted");
        return;
      }

      // Keep track of which files we’ve uploaded so we can clean up on error
      const uploadedFilesUrls = [];
      // Capture answers at start
      const currentUserAnswers = [...answers];

      // Helper to build payload
      const processSubmissionData = async () => {
        const answersPromises = questions.map(async (question, index) => {
          let uploadedUrl = "";
          if (question.questionType === "Upload File") {
            console.log("Uploading file for question", index);
            if (!cvFile) {
              console.error("No CV file selected");
              throw new Error("Please select a CV file before submitting.");
            }
            uploadedUrl = await uploadFileToFirebase(cvFile);
            console.log("File uploaded", uploadedUrl);
            uploadedFilesUrls.push(uploadedUrl);
          } else if (question.questionType === "Upload Video") {
            console.log("Uploading video for question", index);
            if (!videoFile) {
              console.error("No video file selected");
              throw new Error("Please select a video file before submitting.");
            }
            uploadedUrl = await uploadFileToFirebase(videoFile, (progress) => {
              setVideoUploadProgress(progress);
            });
            console.log("Video upload progress", videoUploadProgress);
            console.log("Video uploaded", uploadedUrl);
            uploadedFilesUrls.push(uploadedUrl);
          }

          const answerPayload = {
            questionIndex: question.originalIndex,
            questionAnswer: uploadedUrl || currentUserAnswers[index],
          };
          return answerPayload;
        });

        const resolved = await Promise.all(answersPromises);
        return {
          formName,
          answers: resolved,
        };
      };

      try {
        console.log("Initiating try block for submission");
        setIsSubmitting(true);
        setLoading(true);
        if (isAutoSubmit) {
          setHasAutoSubmitted(true);
          console.log("Marked hasAutoSubmitted = true");
        }

        // Build payload and send
        const submissionData = await processSubmissionData();
        console.log("Submission data ready", submissionData);
        const token = Cookies.get("token") || "";
        console.log("Token retrieved", token ? "present" : "missing");
        const response = await axiosInstance.post(
          `${firebaseAPI}/submissions`,
          { ...submissionData, programName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log("Submission response received", response);

        // Success
        setFormStatus(FormStatus.COMPLETED);
        message.success(
          isAutoSubmit
            ? "Form auto‐submitted successfully!"
            : "Form submitted successfully!"
        );
        console.log("Submission successful");
      } catch (err) {
        console.error("Error submitting form:", err);
        setFormStatus(FormStatus.ERROR);

        const msg =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong. Please try again.";
        setErrorMessage(msg);
        message.error(msg);
        console.log("Error message set", msg);

        // Roll back any uploads
        if (uploadedFilesUrls.length) {
          console.log("Cleaning up uploaded files", uploadedFilesUrls);
          try {
            await Promise.all(
              uploadedFilesUrls.map((url) => deleteFileFromFirebase(url))
            );
            console.log("Uploaded files cleaned up");
          } catch (cleanupErr) {
            console.error("Cleanup failed:", cleanupErr);
          }
        }
      } finally {
        console.log("Finalizing submission, resetting loading flags");
        setLoading(false);
        setIsSubmitting(false);
      }
    },
    [
      answers,
      questions,
      formName,
      programName,
      isSubmitting,
      hasAutoSubmitted,
      cvFile,
      videoFile,
    ]
  );
  const [readyToAutoSubmit, setReadyToAutoSubmit] = useState(false);
  const [serverOffset, setServerOffset] = useState(); // Optional
  const [msRemaining, setMsRemaining] = useState(); // Optional
  const [serverTimestamp, setServerTimestamp] = useState(); // Needed for offset
  const [timerExpiredOnLoad, setTimerExpiredOnLoad] = useState(false); // NEW: Track if timer was already expired

  // First useEffect - Fetching questions and setting up timer
  useEffect(() => {
    const handleGetQuestions = async () => {
      try {
        const token = Cookies.get("token") || "";
        const response = await axiosInstance.get(
          `${firebaseAPI}/forms/${formName}/questions/${programName}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const rawQuestions = response.data.questions;
        const questionsWithOriginalIndex = rawQuestions.map((q, i) => ({
          ...q,
          originalIndex: i,
        }));
        const shuffled = shuffleArray(questionsWithOriginalIndex).map((q) =>
          q.questionType === "Multiple Choice"
            ? { ...q, questionChoices: shuffleArray(q.questionChoices) }
            : q
        );
        setQuestions(shuffled);
        setAnswers(new Array(shuffled.length).fill(""));

        const { trialStartTimestamp, timerMinutes, serverTimestamp } =
          response.data;

        // const trialStartTimestamp = new Date(
        //   "2025-06-13T21:59:06.637Z"
        // ).getTime();
        // const serverTimestamp = new Date("2025-06-13T22:00:06.637Z").getTime();
        // const timerMinutes = 2;
        if (timerMinutes > 0 && trialStartTimestamp && serverTimestamp) {
          const clientUtcNow = Date.now();
          // const clientUtcNow = new Date("2025-06-13T11:00:01.995Z").getTime();
          const offset = clientUtcNow - serverTimestamp;

          // ✅ Compute true end time (on server's timeline)
          const trueEndUtc = trialStartTimestamp + timerMinutes * 60_000;

          // ✅ Remaining time in milliseconds, based on server clock
          const remaining = trueEndUtc - serverTimestamp;

          // ✅ Map remaining time to the client's clock
          const target = clientUtcNow + remaining;

          // logTimerDebugInfo({
          //   clientUtcNow,
          //   serverTimestamp,
          //   offset,
          //   target,
          //   remaining,
          // });

          // 🚨 Timer already expired
          if (remaining <= 0) {
            console.warn(
              "⚠️ Timer has already expired by",
              Math.abs(remaining),
              "ms"
            );
            setFormTimerTarget(-1);
            setMsRemaining(0);
            setServerOffset(offset);
            setServerTimestamp(serverTimestamp);
            setTimerExpiredOnLoad(true);
            setReadyToAutoSubmit(true);
            setTimeout(() => handleFormSubmit(true), 100); // auto-submit
            return;
          }

          // ✅ Timer is valid
          setFormTimerTarget(target);
          setMsRemaining(remaining);
          setServerOffset(offset);
          setServerTimestamp(serverTimestamp);
          setTimerExpiredOnLoad(false);
        } else {
          console.log("ℹ️ No timer configured for this form");
          setFormTimerTarget(-1);
          setMsRemaining(null);
          setServerOffset(null);
          setServerTimestamp(null);
          setTimerExpiredOnLoad(false); // NEW: No timer means not expired
        }

        if (response.data.roundWithoutForm) {
          setFormStatus(FormStatus.NO_FORM_ROUND);
        }

        setReadyToAutoSubmit(true);
      } catch (error) {
        console.error("[fetchQuestions] Failed", error);
        message.error("Failed to fetch questions");
        setFormStatus(FormStatus.ERROR);
        setErrorMessage(
          error.response?.data?.message || "You can't submit at this time."
        );
      }
    };

    handleGetQuestions();
  }, [formName, programName, firebaseAPI]);

  // Second useEffect - Auto-submit logic with comprehensive error handling
  useEffect(() => {
    // Guard clauses - exit early if conditions not met
    if (!readyToAutoSubmit) {
      console.log("[autoSubmit] Not ready to auto-submit yet");
      return;
    }

    if (hasAutoSubmitted) {
      console.log("[autoSubmit] Already auto-submitted");
      return;
    }

    if (formStatus === FormStatus.COMPLETED) {
      console.log("[autoSubmit] Form already completed");
      return;
    }

    if (isSubmitting) {
      console.log("[autoSubmit] Form is currently being submitted");
      return;
    }

    if (formTimerTarget <= 0) {
      console.log("[autoSubmit] No timer configured or timer disabled");
      return;
    }

    if (serverOffset === null || serverOffset === undefined) {
      console.log("[autoSubmit] Server offset not available");
      return;
    }

    // NEW: Skip auto-submit logic if timer was already expired on load
    // The first useEffect already handled the submission
    if (timerExpiredOnLoad) {
      console.log(
        "[autoSubmit] Timer was expired on load, first useEffect handled submission"
      );
      return;
    }

    // ✅ Current client time
    // const clientNow = new Date("2025-06-13T11:00:01.995Z").getTime();
    const clientNow = Date.now();

    // ✅ Adjusted UTC now (matching server time)
    const adjustedUtcNow = clientNow - serverOffset;

    // ✅ Remaining time = Target - Adjusted time
    const remaining = formTimerTarget - clientNow;

    // logAutoSubmitCheck({
    //   clientNow,
    //   adjustedUtcNow,
    //   formTimerTarget,
    //   remaining,
    // });

    // Handle expired timer
    if (remaining <= 0) {
      handleFormSubmit(true);
      return;
    }

    // Handle very short remaining time (less than 1 second)
    if (remaining < 1000) {
      handleFormSubmit(true);
      return;
    }

    // Cap the timeout to prevent JavaScript timer issues
    // JavaScript setTimeout max value is 2147483647 (about 24.8 days)
    const maxTimeout = 2147483647;
    const timeoutMs = Math.min(remaining, maxTimeout);

    console.log(`✅ [autoSubmit] Scheduling auto-submit in ${timeoutMs} ms`);
    console.log(
      `⏱️ [autoSubmit] Will submit at:`,
      new Date(Date.now() + timeoutMs).toISOString()
    );

    const timeout = setTimeout(() => {
      console.log("🔥 [autoSubmit] Timeout fired, submitting form");
      handleFormSubmit(true);
    }, timeoutMs);

    return () => {
      console.log("🧹 [autoSubmit] Cleaning up timeout and interval");
      clearTimeout(timeout);
    };
  }, [
    readyToAutoSubmit,
    hasAutoSubmitted,
    formStatus,
    isSubmitting,
    formTimerTarget,
    serverOffset,
    timerExpiredOnLoad, // NEW: Add to dependency array
    handleFormSubmit,
  ]);
  const handleTimeOutPage = () => {
    setFormStatus(FormStatus.TIMEOUT);
  };

  const renderQuestion = (question, index) => {
    switch (question.questionType) {
      case "Multiple Choice":
        return (
          <Radio.Group
            key={questionKey}
            {...(answers[index] !== undefined ? { value: answers[index] } : {})}
            onChange={(e) => handleAnswerChange(index, e.target.value)} // Handle the change event
            rules={[
              {
                required: true,
                message: "This answer is required",
              },
            ]}
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            {question.questionChoices.map((choice, choiceIndex) => (
              <Radio
                key={`question${index}_${choiceIndex}`}
                value={choice.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                  borderRadius: "4px",
                  border: "1px solid #d9d9d9",
                }}
                rules={[
                  {
                    required: true,
                    message: "This answer is required",
                  },
                ]}
              >
                {choice.type === "text" ? (
                  <span style={{ marginLeft: "10px" }}>{choice.value}</span>
                ) : (
                  <img
                    src={choice.value}
                    alt={`Choice ${choiceIndex}`}
                    style={{
                      maxWidth: "40%",
                      height: "auto",
                      marginLeft: "10px",
                    }}
                  />
                )}
              </Radio>
            ))}
          </Radio.Group>
        );
      case "Text":
        return (
          <Form.Item
            name={`answer_${index}`}
            rules={[
              {
                required: true,
                message: "This answer is required",
              },
            ]}
          >
            <TextArea
              type="text"
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder={`Answer`}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              style={{ width: "100%", marginTop: "15px" }}
            />
          </Form.Item>
        );
      case "Upload File":
        return (
          <div style={{ margin: "15px" }}>
            <Dragger
              beforeUpload={handleFileUpload}
              fileList={cvFile ? [cvFile] : []}
              multiple={false}
              showUploadList={true}
              accept=".pdf,.doc,.docx"
              style={{
                padding: "20px",
                borderRadius: "5px",
                border: "2px dashed #1890ff",
                textAlign: "center",
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              {cvFileName && (
                <p className="ant-upload-text" style={{ fontSize: "12px" }}>
                  {cvFileName}
                </p>
              )}
            </Dragger>
          </div>
        );
      case "Upload Video":
        return (
          <div
            style={{
              margin: "20px auto",
              maxWidth: 400,
              padding: 20,
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              backgroundColor: "#fafafa",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Upload
              beforeUpload={handleVideoUpload}
              fileList={videoFile ? [videoFile] : []}
              accept=".mp4"
              showUploadList={{ showPreviewIcon: false }}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{ width: "100%" }}
              >
                Select Video (Max 200 MB)
              </Button>
            </Upload>

            {videoFileName && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                Selected file: <strong>{videoFileName}</strong>
              </div>
            )}

            {videoUploadProgress > 0 && videoUploadProgress < 100 && (
              <div style={{ marginTop: 16 }}>
                <Progress
                  percent={Math.round(videoUploadProgress)}
                  status="active"
                  strokeColor={{
                    "0%": "#108ee9",
                    "100%": "#87d068",
                  }}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {loading && (
        <BeatLoader
          color="#123abc"
          loading={loading}
          css={override}
          size={15}
        />
      )}
      {formStatus == FormStatus.IN_PROGRESS && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            background: "linear-gradient(to bottom, #f7fafc, #e2e8f0)",
            padding: "20px 0",
          }}
        >
          {formTimerTarget > 0 && (
            <div
              style={{
                marginBottom: "20px",
                background: "rgba(255, 255, 255, 0.9)",
                padding: "12px 20px",
                borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              }}
            >
              <Countdown
                title={
                  <span style={{ fontWeight: "600", color: "#3182ce" }}>
                    Time Remaining
                  </span>
                }
                value={formTimerTarget}
                onFinish={handleTimeOutPage}
                format="mm:ss"
                style={{ fontSize: "16px" }}
              />
            </div>
          )}

          <Card
            style={{
              width: "80vw",
              maxWidth: "1000px",
              padding: "3%",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxSizing: "border-box",
              border: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ width: "100%" }}>
                <Title
                  level={2}
                  style={{
                    textAlign: "center",
                    marginBottom: "30px",
                    width: "100%",
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#2d3748",
                  }}
                >
                  {formName}
                </Title>
                <Form
                  onFinish={handleFormSubmit}
                  style={{
                    width: "100%",
                    padding: "0 5% 5%",
                    boxSizing: "border-box",
                    userSelect: "none",
                  }}
                  onContextMenu={(e) => e.preventDefault()} // Disable right-click
                  onCopy={(e) => e.preventDefault()} // Disable copy
                  onPaste={(e) => e.preventDefault()} // Disable paste
                  onCut={(e) => e.preventDefault()} // Disable cut
                  onKeyDown={(e) => {
                    if (
                      e.ctrlKey &&
                      ["c", "x", "v", "u"].includes(e.key.toLowerCase())
                    ) {
                      e.preventDefault(); // Disable Ctrl+C/X/V/U
                    }
                  }}
                >
                  <div style={{ marginBottom: "30px" }}>
                    <Progress
                      percent={Math.round(
                        (currentStep / questions.length) * 100
                      )}
                      showInfo={false}
                      strokeColor="#1890ff"
                    />
                    <Typography.Text>
                      Question {currentStep + 1} of {questions.length}
                    </Typography.Text>
                  </div>
                  {questions.length > 0 && (
                    <div
                      style={{
                        marginTop: "20px",
                        width: "100%",
                        boxSizing: "border-box",
                        background: "#f8fafc",
                        borderRadius: "12px",
                        padding: "24px",
                        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.03)",
                      }}
                    >
                      <h3
                        style={{
                          padding: "2%",
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#2d3748",
                          lineHeight: "1.4",
                          marginBottom: "16px",
                        }}
                      >
                        {`${questions[currentStep].questionContent}`}{" "}
                        <span style={{ color: "#e53e3e", fontWeight: "bold" }}>
                          *
                        </span>
                      </h3>
                      {questions[currentStep].questionImage && (
                        <div
                          style={{
                            textAlign: "center",
                            marginTop: "16px",
                            marginBottom: "20px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            pointerEvents: "none",
                            WebkitUserDrag: "none",
                            userSelect: "none",
                          }}
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          <img
                            src={questions[currentStep].questionImage}
                            alt={`Question ${currentStep + 1}`}
                            style={{
                              pointerEvents: "none",
                              WebkitUserDrag: "none",
                              userSelect: "none",
                              maxWidth: "100%",
                              height: "auto",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        </div>
                      )}
                      <div style={{ padding: "10px 0" }}>
                        {renderQuestion(questions[currentStep], currentStep)}
                      </div>
                    </div>
                  )}
                  <Form.Item>
                    <div
                      style={{
                        display: "flex",
                        marginTop: "30px",
                        width: "100%",
                        placeContent:
                          currentStep === 0 ? "center" : "space-between",
                      }}
                    >
                      {currentStep > 0 && (
                        <Button
                          size="large"
                          style={{
                            borderRadius: "8px",
                            padding: "0 24px",
                            height: "44px",
                            fontSize: "16px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={prev}
                          icon={<span style={{ marginRight: "6px" }}>←</span>}
                        >
                          Previous
                        </Button>
                      )}
                      {currentStep < questions.length - 1 && (
                        <Button
                          type="primary"
                          size="large"
                          style={{
                            borderRadius: "8px",
                            padding: "0 24px",
                            height: "44px",
                            fontSize: "16px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            background: "#4299e1",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={next}
                        >
                          Next <span style={{ marginLeft: "6px" }}>→</span>
                        </Button>
                      )}
                      {currentStep === questions.length - 1 && (
                        <>
                          <Button
                            type="primary"
                            size="large"
                            onClick={showConfirm}
                            style={{
                              borderRadius: "8px",
                              padding: "0 30px",
                              height: "44px",
                              fontSize: "16px",
                              fontWeight: "600",
                              background: "#38a169",
                              border: "none",
                              boxShadow: "0 4px 6px rgba(56,161,105,0.3)",
                            }}
                          >
                            Submit
                          </Button>

                          <Modal
                            title="Are you sure?"
                            open={isModalVisible}
                            onOk={handleOk}
                            onCancel={handleCancel}
                            okText="Yes, Submit"
                            cancelText="Cancel"
                          >
                            <p>Are you sure you want to submit your answers?</p>
                          </Modal>
                        </>
                      )}
                    </div>
                  </Form.Item>
                </Form>
              </div>
            </div>
          </Card>
        </div>
      )}
      {formStatus == FormStatus.TIMEOUT && (
        <Result
          status="warning"
          title="You have exceeded the time limit for your trial"
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() => (window.location.href = `/${programName}/`)}
            >
              Go to homepage
            </Button>
          }
        />
      )}

      {formStatus == FormStatus.NO_FORM_ROUND && (
        <Result
          status="warning"
          title="This Round does not have a form, we will contact you."
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() => (window.location.href = `/${programName}/`)}
            >
              Go to homepage
            </Button>
          }
        />
      )}

      {formStatus == FormStatus.ERROR && (
        <Result
          status="error"
          title={errorMessage}
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() => (window.location.href = `/${programName}/`)}
            >
              Go to homepage
            </Button>
          }
        />
      )}

      {formStatus == FormStatus.COMPLETED && (
        <>
          <div className="success-container flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-white px-4">
            <Confetti
              recycle={false}
              numberOfPieces={200}
              gravity={0.15}
              colors={["#4CAF50", "#2196F3", "#FFC107", "#E91E63", "#9C27B0"]}
            />
            <div className="success-card bg-white rounded-xl shadow-lg p-10 max-w-lg w-full text-center border-t-4 border-green-500 animate-fade-in-up">
              <div className="success-icon mb-8 inline-flex">
                <div className="rounded-full bg-green-100 p-5">
                  <CheckCircleFilled className="text-green-500 text-5xl" />
                </div>
              </div>
              <Result
                status="success"
                title={
                  <h1 className="text-3xl font-bold text-gray-800">
                    Application Submitted!
                  </h1>
                }
                subTitle={
                  <div className="mt-3">
                    <p className="text-gray-600 text-lg">
                      Thank you for your submission.
                    </p>
                    <p className="text-gray-600 text-lg mt-2">
                      We will review your application and notify you of the next
                      steps.
                    </p>
                  </div>
                }
                extra={
                  <div className="mt-10 space-y-4">
                    <Button
                      type="primary"
                      size="large"
                      key="track"
                      className="w-full bg-green-500 hover:bg-green-600 border-none shadow-md h-12 text-base font-medium"
                      onClick={() =>
                        (window.location.href = `/${programName}/track`)
                      }
                    >
                      Track Your Application
                    </Button>
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuizAndCVPage;
